import { useMemo, useState } from "react";
import { ErrorModal, PaymentSelectorContent } from "@apac/components/checkout";
import type { Product } from "@b2bportal/purchase-api";
import type { ParentContextWithCardPayment } from "@checkout/states/Payments/CardPayment/types";
import {
  CardPaymentSelectors,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { TokenizeCardErrors } from "@hopper-b2b/types";
import { B2BSpinner, LoadingIndicator } from "@hopper-b2b/ui";
import type { State } from "xstate";
import { Event, type TEvent } from "../../modules/events";
import "./styles.scss";
import { useTranslatedSpreedlyError } from "./useTranslatedError";

export const PaymentInformation = ({
  disabled,
  onContinue,
  product,
  yearDigit,
  showLoadingIndicator = true,
}: {
  disabled?: boolean;
  onContinue?: () => void;
  product?: Product;
  yearDigit?: "two" | "four";
  showLoadingIndicator?: boolean;
}) => {
  const { t } = useI18nContext();
  const { getTranslatedSpreedlyError } = useTranslatedSpreedlyError();

  const [tokenizeErrors, setTokenizeErrors] = useState<TokenizeCardErrors[]>(
    []
  );

  const send = useCheckoutSend<TEvent>();

  const isPaymentLoading =
    useSelector(CardPaymentSelectors.getIsPaymentLoading) &&
    showLoadingIndicator;

  const loadingMessage = useSelector(
    (state: State<ParentContextWithCardPayment>) =>
      CardPaymentSelectors.getLoadingMessage(state, t)
  );

  const isPaymentError = useSelector(CardPaymentSelectors.getIsPaymentError);
  const [spreedlyError, setSpreedlyError] = useState(false);

  const handleCloseErrorPopup = () => {
    setSpreedlyError(false);
    send(Event.CLEAR_PAYMENT_ERROR);
  };

  const creditCards = useSelector(CardPaymentSelectors.getPaymentMethods);

  const selectedPaymentMethodId = useSelector(
    CardPaymentSelectors.getSelectedPaymentMethodId
  );

  const handleContinue = () => {
    onContinue?.();
    send(Event.NEXT);
  };

  const hasPaymentMethods = creditCards?.length > 0;

  const content = isPaymentLoading ? (
    <LoadingIndicator
      message={loadingMessage || ""}
      indicatorSize={"small"}
      indicator={B2BSpinner}
    />
  ) : (
    <PaymentSelectorContent
      setSpreedlyError={setSpreedlyError}
      setTokenizeErrors={setTokenizeErrors}
      onContinue={handleContinue}
      isContinueDisabled={!hasPaymentMethods || !selectedPaymentMethodId}
      disabled={disabled}
      product={product}
      yearDigit={yearDigit}
    />
  );

  const tokenizeError = useMemo(() => {
    if (tokenizeErrors.length > 0) {
      const error = getTranslatedSpreedlyError(tokenizeErrors[0]);
      if (error != null) {
        return error;
      }
      return tokenizeErrors[0].message;
    } else {
      return t("addPaymentAgain");
    }
  }, [tokenizeErrors, t, getTranslatedSpreedlyError]);

  return (
    <div className="payment">
      {content}

      <ErrorModal
        className="add-payment-method-error-modal"
        open={isPaymentError || spreedlyError}
        title={t("unableToAddPayment")}
        subtitle={tokenizeError}
        primaryButtonText={t("tryAgain")}
        primaryOnClick={handleCloseErrorPopup}
        onClose={handleCloseErrorPopup}
      />
    </div>
  );
};
